import { useState, useEffect } from "react";
import { fetchBrands } from "../../../services/brandService";
import type { BrandResponse } from "../../../types/BrandModels";

export type BrandStatus = "idle" | "loading" | "valid" | "missing";

export function hasValidBrandDetails(brand: BrandResponse): boolean {
  // Only the name gates Chat and Create Agent (SGL-448). Website, service
  // areas and brand voice make the agents better, but requiring them locked a
  // real new customer out of the product on his first day; the agents ask for
  // what they're missing instead.
  return !!brand.brand?.name?.trim();
}

/**
 * Returns brand status for the given customer. Use to gate Create Agent, Chat, etc.
 * - idle: no customer selected
 * - loading: fetching brand
 * - valid: brand has required details (name, website, service_areas, brand_voice)
 * - missing: no brand or incomplete
 */
export function useBrandStatus(
  customerId: string | null,
  idToken: string | null
): BrandStatus {
  const [brandStatus, setBrandStatus] = useState<BrandStatus>("idle");

  useEffect(() => {
    if (!customerId || !idToken) {
      setBrandStatus("idle");
      return;
    }
    let cancelled = false;
    setBrandStatus("loading");
    (async () => {
      try {
        const res = await fetchBrands(customerId, idToken);
        if (cancelled) return;
        const brand = res?.brands?.[0] as BrandResponse | undefined;
        if (!brand || !hasValidBrandDetails(brand)) {
          setBrandStatus("missing");
        } else {
          setBrandStatus("valid");
        }
      } catch {
        if (!cancelled) setBrandStatus("missing");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, idToken]);

  return brandStatus;
}
